import React, { useState, useEffect, useRef, useContext } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useInView } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import {
    Zap, Palette, Smartphone, Globe, Star, Users, ArrowRight,
    Play, Check, Crown, Sparkles, Target, Shield, Rocket,
    Monitor, Edit, Layout, Image, ShoppingBag, Heart,
    ChevronRight, Eye, MousePointer, Download, Upload,
    Code, Layers, Settings, Coffee, TrendingUp, Award
} from 'lucide-react';
import UniversalNavbar from '../components/UniversalNavbar';
import VFooter from '../components/VFooter';

const WebsiteBuilderLanding = () => {
    const { user, authToken } = useContext(AuthContext);
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const heroRef = useRef(null);
    const { scrollY } = useScroll();
    const y1 = useTransform(scrollY, [0, 300], [0, 50]);
    const y2 = useTransform(scrollY, [0, 300], [0, -50]);
    const processRef = useRef(null);
    const isProcessInView = useInView(processRef, { once: true, margin: "-100px" });

    // Mouse tracking for interactive hero
    useEffect(() => {
        const handleMouseMove = (e) => {
            setMousePosition({ x: e.clientX, y: e.clientY });
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    // Authentication helper functions
    const handleAuthenticatedAction = (targetPath) => {
        if (user && authToken) {
            navigate(targetPath);
        } else {
            navigate('/login', { state: { from: targetPath } });
        }
    };

    const handleTemplateClick = (templateId) => {
        handleAuthenticatedAction(`/template${templateId}`);
    };

    const handleViewMoreTemplates = () => {
        handleAuthenticatedAction('/TemplateThemes');
    };

    const handleSubscriptionClick = () => {
        handleAuthenticatedAction('/subscription');
    };

    const templates = [
        { id: 1, name: 'Minimalist', image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop', category: 'Clean & Simple' },
        { id: 2, name: 'Modern', image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop', category: 'Contemporary' },
        { id: 3, name: 'Banner Pro', image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop', category: 'Visual Impact' },
        { id: 15, name: 'Bohemian', image: 'https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=400&h=300&fit=crop', category: 'Artistic' },
        { id: 16, name: 'Prestige', image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=300&fit=crop', category: 'Luxury' },
        { id: 17, name: 'Nordic', image: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400&h=300&fit=crop', category: 'Sustainable' },
        { id: 18, name: 'Focal', image: 'https://images.unsplash.com/photo-1452587925148-ce544e77e70d?w=400&h=300&fit=crop', category: 'Photography' },
        { id: 19, name: 'Broadcast', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop', category: 'Editorial' },
        { id: 20, name: 'Studio', image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop', category: 'Creative' }
    ];

    const processSteps = [
        {
            title: "Choose Your Perfect Template",
            description: "Browse 20+ stunning templates designed specifically for fashion brands",
            icon: Eye,
            demo: "template-selection",
            color: "from-primary to-primary-dark"
        },
        {
            title: "Customize Everything",
            description: "Add your brand, products, and story with our powerful drag-drop editor",
            icon: Palette,
            demo: "content-editing",
            color: "from-primary-light to-primary"
        },
        {
            title: "Launch & Dominate",
            description: "Publish your professional website and start attracting customers instantly",
            icon: Rocket,
            demo: "website-live",
            color: "from-primary-dark to-primary"
        }
    ];

    const features = [
        {
            icon: Smartphone,
            title: "Mobile-First Design",
            desc: "Every template looks stunning on phones, tablets, and desktops",
            gradient: "from-blue-500 to-blue-600"
        },
        {
            icon: Zap,
            title: "Lightning Fast Loading",
            desc: "Optimized for speed - your customers won't wait",
            gradient: "from-yellow-500 to-orange-500"
        },
        {
            icon: Palette,
            title: "Complete Brand Control",
            desc: "Customize colors, fonts, and layouts to match your vision",
            gradient: "from-purple-500 to-pink-500"
        },
        {
            icon: ShoppingBag,
            title: "E-commerce Powerhouse",
            desc: "Accept payments, manage inventory, track orders",
            gradient: "from-green-500 to-emerald-500"
        },
        {
            icon: TrendingUp,
            title: "SEO Domination",
            desc: "Built-in SEO tools to rank higher on Google",
            gradient: "from-indigo-500 to-purple-500"
        },
        {
            icon: Award,
            title: "Professional Quality",
            desc: "Designer-grade templates that convert visitors to customers",
            gradient: "from-red-500 to-pink-500"
        }
    ];

    const stats = [
        { number: "20+", label: "Designer Templates", icon: Layout, suffix: "" },
        { number: "70+", label: "Happy Customers", icon: Users, suffix: "" },
        { number: "2", label: "Minutes Setup", icon: Zap, suffix: "MIN" },
        { number: "99.9", label: "Uptime Guarantee", icon: Shield, suffix: "%" }
    ];

    const pricingPlans = [
        {
            name: "Starter",
            price: "₹987",
            period: "month",
            description: "Perfect for new fashion brands",
            features: [
                "5 Premium Templates",
                "Basic Customization",
                "Mobile Responsive",
                "SSL Security",
                "Email Support"
            ],
            popular: false,
            cta: "Get Started"
        },
        {
            name: "Professional",
            price: "₹5389",
            period: "6 months",
            description: "Best for growing fashion businesses",
            features: [
                "All 20 Templates",
                "Advanced Customization",
                "E-commerce Features",
                "SEO Optimization",
                "Priority Support",
                "Custom Domain",
                "Analytics Dashboard"
            ],
            popular: true,
            cta: "Start Building"
        },
        {
            name: "Enterprise",
            price: "₹9687",
            period: "year",
            description: "For established fashion enterprises",
            features: [
                "Everything in Professional",
                "White Label Solution",
                "API Access",
                "Advanced Analytics",
                "Dedicated Manager",
                "Custom Integrations",
                "Priority Development"
            ],
            popular: false,
            cta: "Contact Sales"
        }
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            if (isPlaying) {
                setCurrentStep((prev) => (prev + 1) % processSteps.length);
            }
        }, 4000);

        return () => clearInterval(interval);
    }, [isPlaying, processSteps.length]);

    const startDemo = () => {
        setIsPlaying(true);
        setCurrentStep(0);
    };

    return (
        <div className="bg-white overflow-x-hidden font-primary">
            <UniversalNavbar />

            {/* Enhanced Hero Section */}
            <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 px-4 sm:px-6 lg:px-8 xl:px-[7%] bg-gradient-to-br from-primary-light via-white to-primary-light overflow-hidden">
                {/* Enhanced Decorative Elements */}
                <motion.div
                    className="absolute top-10 right-[-10%] w-[300px] h-[300px] lg:w-[500px] lg:h-[500px] opacity-10"
                    style={{ background: `linear-gradient(135deg, #ca7751, #f4ece5)` }}
                    initial={{ scale: 0, rotate: -45, opacity: 0 }}
                    animate={{
                        scale: [0, 1.2, 1],
                        rotate: [45, -10, 0],
                        opacity: [0, 0.15, 0.1]
                    }}
                    transition={{
                        duration: 3,
                        ease: "easeOut",
                        times: [0, 0.7, 1]
                    }}
                >
                    <div className="w-full h-full rounded-full"></div>
                </motion.div>

                {/* Additional floating elements */}
                <motion.div
                    className="absolute top-32 left-[-5%] w-[150px] h-[150px] bg-primary opacity-5 rounded-full"
                    animate={{
                        y: [0, -20, 0],
                        scale: [1, 1.1, 1],
                    }}
                    transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                />

                <motion.div
                    className="absolute bottom-20 right-[10%] w-[100px] h-[100px] bg-gradient-to-tr from-primary to-primary-light opacity-10 rounded-lg"
                    animate={{
                        rotate: [0, 45, 0],
                        scale: [1, 1.2, 1],
                    }}
                    transition={{
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1
                    }}
                />

                <div className="max-w-7xl mx-auto">
                    <div className="text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="inline-flex items-center px-4 py-2 mb-6 rounded-full border border-primary bg-primary-light text-primary"
                            whileHover={{ scale: 1.05, y: -2 }}
                        >
                            <motion.div
                                animate={{ rotate: [0, 10, -10, 0] }}
                                transition={{ duration: 2, repeat: Infinity }}
                            >
                                <Crown className="w-4 h-4 mr-2" />
                            </motion.div>
                            <span className="text-sm font-semibold">Launch Your Fashion Empire</span>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 leading-tight text-dark"
                        >
                            Create Your Dream{' '}
                            <span className="relative inline-block text-primary">
                                Fashion Website
                                <motion.div
                                    className="absolute inset-0 -z-10 rounded-lg opacity-20 bg-primary-light"
                                    initial={{ scaleX: 0 }}
                                    animate={{ scaleX: 1 }}
                                    transition={{ duration: 0.8, delay: 0.8 }}
                                />
                            </span>
                            {' '}in Minutes
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="text-lg sm:text-xl lg:text-2xl mb-8 leading-relaxed max-w-4xl mx-auto text-muted"
                        >
                            Choose from 20+ stunning templates, customize with your brand, and launch a professional fashion website that converts visitors into customers.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
                        >
                            <motion.div
                                whileHover={{ scale: 1.05, y: -2 }}
                                whileTap={{ scale: 0.98 }}
                                transition={{ type: "spring", stiffness: 400, damping: 17 }}
                            >
                                <Link
                                    to="/register"
                                    className="group inline-flex items-center justify-center px-8 py-4 text-white rounded-xl font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 bg-primary hover:bg-primary-dark relative overflow-hidden"
                                >
                                    <motion.div
                                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20"
                                        initial={{ x: '-100%' }}
                                        whileHover={{ x: '100%' }}
                                        transition={{ duration: 0.6 }}
                                    />
                                    <span className="relative z-10">Start Building Now</span>
                                    <motion.div
                                        className="ml-2 relative z-10"
                                        animate={{ x: [0, 3, 0] }}
                                        transition={{ duration: 1.5, repeat: Infinity }}
                                    >
                                        <ArrowRight className="w-5 h-5" />
                                    </motion.div>
                                </Link>
                            </motion.div>
                            {/* <button
                                onClick={startDemo}
                                className="group inline-flex items-center justify-center px-8 py-4 border-2 border-primary text-primary rounded-xl font-semibold text-lg transition-all duration-300 hover:bg-primary hover:text-white"
                            >
                                <Play className="mr-2 w-5 h-5" />
                                Watch Demo
                            </button> */}
                        </motion.div>

                        {/* Trust Indicators */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.8 }}
                            className="flex flex-wrap justify-center items-center gap-6 text-sm text-muted"
                        >
                            <div className="flex items-center">
                                <Check className="w-4 h-4 mr-2 text-green-500" />
                                Professional Templates
                            </div>
                            <div className="flex items-center">
                                <Check className="w-4 h-4 mr-2 text-green-500" />
                                Mobile Responsive
                            </div>
                            <div className="flex items-center">
                                <Check className="w-4 h-4 mr-2 text-green-500" />
                                SEO Optimized
                            </div>
                        </motion.div>
                    </div>

                    {/* Stats Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1 }}
                        className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6"
                    >
                        {stats.map((stat, index) => (
                            <motion.div
                                key={index}
                                className="text-center p-6 rounded-xl bg-white shadow-lg border border-gray-200"
                                whileHover={{ y: -5, shadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                            >
                                <stat.icon className="w-8 h-8 mx-auto mb-3 text-primary" />
                                <div className="text-3xl font-bold mb-1 text-primary">
                                    {stat.number}
                                </div>
                                <div className="text-sm font-medium text-muted">
                                    {stat.label}
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Animated Process Section */}
            <section className="py-20 md:py-32 px-4 sm:px-6 lg:px-8 xl:px-[7%] bg-primary-light">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-dark">
                            See How Easy It Is
                        </h2>
                        <p className="text-lg lg:text-xl max-w-2xl mx-auto text-muted">
                            Watch your website come to life in real-time with our simple 3-step process
                        </p>
                    </div>

                    {/* Interactive Demo */}
                    <div className="mb-16">
                        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                            {/* Demo Header */}
                            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                                </div>
                                <div className="text-sm font-medium text-muted">
                                    Website Builder Demo
                                </div>
                                <button
                                    onClick={startDemo}
                                    className="flex items-center text-sm font-medium transition-colors text-primary hover:text-primary-dark"
                                >
                                    <Play className="w-4 h-4 mr-1" />
                                    Start
                                </button>
                            </div>

                            {/* Demo Content */}
                            <div className="p-6 lg:p-12">
                                <AnimatePresence mode="wait">
                                    {/* Step 1: Template Selection */}
                                    {currentStep === 0 && (
                                        <motion.div
                                            key="template-selection"
                                            initial={{ opacity: 0, x: 50 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -50 }}
                                            transition={{ duration: 0.5 }}
                                        >
                                            <div className="text-center mb-8">
                                                <h3 className="text-2xl font-bold mb-2 text-primary">
                                                    Step 1: Choose Your Template
                                                </h3>
                                                <p className="text-muted">
                                                    Pick from our collection of professionally designed templates
                                                </p>
                                            </div>
                                            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                                                {templates.slice(0, 6).map((template, index) => (
                                                    <motion.div
                                                        key={template.id}
                                                        className={`aspect-[4/3] rounded-lg overflow-hidden shadow-md cursor-pointer hover:scale-105 transition-all border-2 ${index === 0 ? 'border-primary' : 'border-transparent'}`}
                                                        whileHover={{ scale: 1.05 }}
                                                        initial={{ opacity: 0, scale: 0.8 }}
                                                        animate={{ opacity: 1, scale: 1 }}
                                                        transition={{ delay: index * 0.1 }}
                                                    >
                                                        <img
                                                            src={template.image}
                                                            alt={template.name}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}

                                    {/* Step 2: Content Editing */}
                                    {currentStep === 1 && (
                                        <motion.div
                                            key="content-editing"
                                            initial={{ opacity: 0, x: 50 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -50 }}
                                            transition={{ duration: 0.5 }}
                                        >
                                            <div className="text-center mb-8">
                                                <h3 className="text-2xl font-bold mb-2 text-primary">
                                                    Step 2: Customize Your Content
                                                </h3>
                                                <p className="text-muted">
                                                    Add your brand, products, and story with our intuitive editor
                                                </p>
                                            </div>
                                            <div className="bg-gray-50 rounded-xl p-6">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    <div className="space-y-4">
                                                        <div className="flex items-center space-x-3">
                                                            <Upload className="w-5 h-5 text-primary" />
                                                            <span className="font-medium">Upload Logo</span>
                                                            <motion.div
                                                                className="ml-auto w-3 h-3 rounded-full bg-green-400"
                                                                initial={{ scale: 0 }}
                                                                animate={{ scale: 1 }}
                                                                transition={{ delay: 0.5 }}
                                                            />
                                                        </div>
                                                        <div className="flex items-center space-x-3">
                                                            <Palette className="w-5 h-5 text-primary" />
                                                            <span className="font-medium">Set Brand Colors</span>
                                                            <motion.div
                                                                className="ml-auto w-3 h-3 rounded-full bg-green-400"
                                                                initial={{ scale: 0 }}
                                                                animate={{ scale: 1 }}
                                                                transition={{ delay: 1 }}
                                                            />
                                                        </div>
                                                        <div className="flex items-center space-x-3">
                                                            <ShoppingBag className="w-5 h-5 text-primary" />
                                                            <span className="font-medium">Add Products</span>
                                                            <motion.div
                                                                className="ml-auto w-3 h-3 rounded-full bg-green-400"
                                                                initial={{ scale: 0 }}
                                                                animate={{ scale: 1 }}
                                                                transition={{ delay: 1.5 }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center justify-center">
                                                        <motion.div
                                                            className="w-32 h-24 bg-gradient-to-br from-primary-light to-primary rounded-lg shadow-md flex items-center justify-center"
                                                            initial={{ scale: 0.8, opacity: 0.5 }}
                                                            animate={{ scale: 1, opacity: 1 }}
                                                            transition={{ duration: 1 }}
                                                        >
                                                            <Monitor className="w-8 h-8 text-white" />
                                                        </motion.div>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}

                                    {/* Step 3: Website Live */}
                                    {currentStep === 2 && (
                                        <motion.div
                                            key="website-live"
                                            initial={{ opacity: 0, x: 50 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -50 }}
                                            transition={{ duration: 0.5 }}
                                        >
                                            <div className="text-center mb-8">
                                                <h3 className="text-2xl font-bold mb-2 text-primary">
                                                    Step 3: Your Website is Live!
                                                </h3>
                                                <p className="text-muted">
                                                    Congratulations! Your professional website is now live and ready for customers
                                                </p>
                                            </div>
                                            <div className="text-center">
                                                <motion.div
                                                    className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-6 bg-green-100"
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    transition={{ type: "spring", duration: 0.8 }}
                                                >
                                                    <Check className="w-12 h-12 text-green-600" />
                                                </motion.div>
                                                <motion.div
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.5 }}
                                                    className="space-y-4"
                                                >
                                                    <p className="text-lg font-semibold text-primary">
                                                        🎉 Website Successfully Created!
                                                    </p>
                                                    <p className="text-muted">
                                                        Your fashion website is now live at: yourstore.com
                                                    </p>
                                                    <div className="flex justify-center space-x-4 pt-4">
                                                        <div className="flex items-center text-sm text-muted">
                                                            <Globe className="w-4 h-4 mr-2" />
                                                            Mobile Optimized
                                                        </div>
                                                        <div className="flex items-center text-sm text-muted">
                                                            <Shield className="w-4 h-4 mr-2" />
                                                            SSL Secured
                                                        </div>
                                                        <div className="flex items-center text-sm text-muted">
                                                            <Zap className="w-4 h-4 mr-2" />
                                                            SEO Ready
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Progress Bar */}
                            <div className="px-6 py-4 border-t border-gray-200">
                                <div className="flex justify-between items-center">
                                    {processSteps.map((_, index) => (
                                        <div key={index} className="flex items-center">
                                            <motion.div
                                                className={`w-3 h-3 rounded-full ${
                                                    currentStep >= index ? 'bg-primary' : 'bg-gray-300'
                                                }`}
                                                animate={{
                                                    scale: currentStep === index ? 1.2 : 1,
                                                }}
                                            />
                                            {index < processSteps.length - 1 && (
                                                <div
                                                    className={`w-8 h-px mx-2 ${
                                                        currentStep > index ? 'bg-primary' : 'bg-gray-300'
                                                    }`}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Process Steps */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {processSteps.map((step, index) => (
                            <motion.div
                                key={index}
                                className="text-center p-6"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.2 }}
                            >
                                <div className="w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center bg-primary-light">
                                    <step.icon className="w-8 h-8 text-primary" />
                                </div>
                                <h3 className="text-xl font-bold mb-2 text-primary">
                                    {step.title}
                                </h3>
                                <p className="text-muted">
                                    {step.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Templates Showcase */}
            <section className="py-20 md:py-32 px-4 sm:px-6 lg:px-8 xl:px-[7%] bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-primary">
                            20+ Stunning Templates
                        </h2>
                        <p className="text-lg lg:text-xl max-w-3xl mx-auto text-muted">
                            Every template is crafted by professional designers and optimized for fashion brands
                        </p>
                    </div>

                    {/* Desktop Grid */}
                    <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {templates.slice(0, 6).map((template, index) => (
                            <motion.div
                                key={template.id}
                                className="group relative bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 cursor-pointer"
                                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{
                                    duration: 0.7,
                                    delay: index * 0.15,
                                    type: "spring",
                                    bounce: 0.3
                                }}
                                whileHover={{
                                    y: -12,
                                    scale: 1.02,
                                    rotateY: 5,
                                    transition: { duration: 0.3 }
                                }}
                                onClick={() => handleTemplateClick(template.id)}
                            >
                                <div className="relative aspect-[4/3] overflow-hidden">
                                    <img
                                        src={template.image}
                                        alt={template.name}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                    />
                                    <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
                                    <div className="absolute top-4 left-4">
                                        <span className="px-3 py-1 rounded-full text-xs font-semibold text-white bg-primary">
                                            {template.category}
                                        </span>
                                    </div>
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <button
                                            className="px-6 py-3 bg-white text-black rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                                        >
                                            Preview Template
                                        </button>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <h3 className="text-xl font-bold mb-2 text-primary">
                                        {template.name}
                                    </h3>
                                    <p className="text-sm mb-3 text-muted">
                                        Perfect for {template.category.toLowerCase()} fashion brands
                                    </p>
                                    <div className="flex items-center justify-between">
                                        <div className="flex space-x-1">
                                            {[...Array(5)].map((_, i) => (
                                                <Star key={i} className="w-4 h-4 fill-current text-yellow-400" />
                                            ))}
                                        </div>
                                        <button className="text-sm font-semibold hover:underline text-primary">
                                            Use Template
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Mobile Horizontal Scroll */}
                    <div className="md:hidden">
                        <div className="flex space-x-6 overflow-x-auto pb-6 px-4 scrollbar-hide snap-x snap-mandatory">
                            {templates.slice(0, 6).map((template, index) => (
                                <motion.div
                                    key={template.id}
                                    className="group relative bg-white rounded-xl shadow-lg overflow-hidden flex-shrink-0 w-72 cursor-pointer snap-center"
                                    initial={{ opacity: 0, x: 50 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{
                                        duration: 0.6,
                                        delay: index * 0.1,
                                        type: "spring"
                                    }}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => handleTemplateClick(template.id)}
                                >
                                    <div className="relative aspect-[4/3] overflow-hidden">
                                        <img
                                            src={template.image}
                                            alt={template.name}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                        />
                                        <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
                                        <div className="absolute top-4 left-4">
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold text-white bg-primary">
                                                {template.category}
                                            </span>
                                        </div>
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                            <motion.div
                                                className="px-6 py-3 bg-white text-black rounded-lg font-semibold shadow-lg"
                                                whileHover={{ scale: 1.1 }}
                                                whileTap={{ scale: 0.95 }}
                                            >
                                                Select Template
                                            </motion.div>
                                        </div>
                                    </div>
                                    <div className="p-4">
                                        <h3 className="text-lg font-bold mb-1 text-primary">
                                            {template.name}
                                        </h3>
                                        <p className="text-sm text-muted">
                                            Perfect for {template.category.toLowerCase()} brands
                                        </p>
                                        <div className="flex justify-between items-center mt-3">
                                            <div className="flex space-x-1">
                                                {[...Array(5)].map((_, i) => (
                                                    <Star key={i} className="w-3 h-3 fill-current text-yellow-400" />
                                                ))}
                                            </div>
                                            <span className="text-xs text-primary font-semibold">Tap to Use</span>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                        <style jsx global>{`
                            .scrollbar-hide {
                                -ms-overflow-style: none;
                                scrollbar-width: none;
                            }
                            .scrollbar-hide::-webkit-scrollbar {
                                display: none;
                            }
                        `}</style>
                    </div>

                    <div className="text-center mt-12">
                        <button
                            onClick={handleViewMoreTemplates}
                            className="inline-flex items-center px-8 py-4 text-white rounded-xl font-semibold text-lg shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 bg-primary hover:bg-primary-dark"
                        >
                            View More Templates
                            <ArrowRight className="ml-2 w-5 h-5" />
                        </button>
                    </div>

                    <div className="text-center mt-8 hidden">
                        <Link
                            to="/templates"
                            className="inline-flex items-center px-8 py-4 text-white rounded-xl font-semibold text-lg shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 bg-primary hover:bg-primary-dark"
                        >
                            View All Templates
                            <ArrowRight className="ml-2 w-5 h-5" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 md:py-32 px-4 sm:px-6 lg:px-8 xl:px-[7%] bg-primary-light">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-dark">
                            Everything You Need to Succeed
                        </h2>
                        <p className="text-lg lg:text-xl max-w-3xl mx-auto text-muted">
                            Built-in features that help fashion brands grow and thrive online
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {features.map((feature, index) => (
                            <motion.div
                                key={index}
                                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                whileHover={{ y: -5 }}
                            >
                                <div className="w-12 h-12 mb-6 rounded-xl flex items-center justify-center bg-primary-light">
                                    <feature.icon className="w-6 h-6 text-primary" />
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-primary">
                                    {feature.title}
                                </h3>
                                <p className="text-muted">
                                    {feature.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section className="py-20 md:py-32 px-4 sm:px-6 lg:px-8 xl:px-[7%] bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-primary">
                            Simple, Transparent Pricing
                        </h2>
                        <p className="text-lg lg:text-xl max-w-3xl mx-auto text-muted">
                            Start building, scale as you grow. No hidden fees, no surprises.
                        </p>
                    </div>

                    {/* Desktop Pricing Grid */}
                    <div className="hidden md:grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        {pricingPlans.map((plan, index) => (
                            <motion.div
                                key={index}
                                className={`relative bg-white rounded-2xl shadow-lg border-2 p-8 ${plan.popular ? 'transform scale-105 border-primary' : 'border-gray-200'}`}
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                whileHover={{ y: -5 }}
                            >
                                {plan.popular && (
                                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-6 py-2 text-white text-sm font-semibold rounded-full bg-primary">
                                        Most Popular
                                    </div>
                                )}

                                <div className="text-center mb-8">
                                    <h3 className="text-2xl font-bold mb-2 text-primary">
                                        {plan.name}
                                    </h3>
                                    <p className="text-sm mb-4 text-muted">
                                        {plan.description}
                                    </p>
                                    <div className="flex items-baseline justify-center">
                                        <span className="text-4xl font-bold text-primary">
                                            {plan.price}
                                        </span>
                                        <span className="text-lg ml-1 text-muted">
                                            /{plan.period}
                                        </span>
                                    </div>
                                </div>

                                <ul className="space-y-4 mb-8">
                                    {plan.features.map((feature, featureIndex) => (
                                        <li key={featureIndex} className="flex items-center">
                                            <Check className="w-5 h-5 mr-3 text-green-500" />
                                            <span className="text-dark">
                                                {feature}
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                <button
                                    onClick={handleSubscriptionClick}
                                    className={`w-full py-4 rounded-xl font-semibold text-lg transition-all duration-300 inline-flex items-center justify-center ${
                                        plan.popular
                                            ? 'text-white shadow-lg hover:shadow-xl bg-primary hover:bg-primary-dark'
                                            : 'border-2 border-primary text-primary hover:bg-primary hover:text-white'
                                    }`}
                                >
                                    {plan.cta}
                                </button>
                            </motion.div>
                        ))}
                    </div>

                    {/* Mobile Horizontal Scroll for Pricing */}
                    <div className="md:hidden">
                        <div className="flex space-x-6 overflow-x-auto pb-6 px-4 scrollbar-hide snap-x snap-mandatory">
                            {pricingPlans.map((plan, index) => (
                                <motion.div
                                    key={index}
                                    className={`relative bg-white rounded-2xl shadow-lg border-2 p-6 flex-shrink-0 w-80 snap-center ${
                                        plan.popular ? 'border-primary' : 'border-gray-200'
                                    }`}
                                    initial={{ opacity: 0, x: 50 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{
                                        duration: 0.6,
                                        delay: index * 0.1,
                                        type: "spring"
                                    }}
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    {plan.popular && (
                                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 text-white text-xs font-semibold rounded-full bg-primary">
                                            Most Popular
                                        </div>
                                    )}

                                    <div className="text-center mb-6">
                                        <h3 className="text-xl font-bold mb-2 text-primary">
                                            {plan.name}
                                        </h3>
                                        <p className="text-sm mb-4 text-muted">
                                            {plan.description}
                                        </p>
                                        <div className="flex items-baseline justify-center">
                                            <span className="text-3xl font-bold text-primary">
                                                {plan.price}
                                            </span>
                                            <span className="text-base ml-1 text-muted">
                                                /{plan.period}
                                            </span>
                                        </div>
                                    </div>

                                    <ul className="space-y-3 mb-6">
                                        {plan.features.map((feature, featureIndex) => (
                                            <li key={featureIndex} className="flex items-center">
                                                <Check className="w-4 h-4 mr-2 text-green-500 flex-shrink-0" />
                                                <span className="text-sm text-dark">
                                                    {feature}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>

                                    <motion.button
                                        onClick={handleSubscriptionClick}
                                        className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                                            plan.popular
                                                ? 'text-white bg-primary shadow-lg'
                                                : 'border-2 border-primary text-primary'
                                        }`}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        {plan.cta}
                                    </motion.button>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    <div className="text-center mt-12">
                        <p className="text-lg text-muted">
                            All plans include SSL certificate, mobile optimization, and 24/7 support
                        </p>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-20 md:py-32 px-4 sm:px-6 lg:px-8 xl:px-[7%] bg-primary">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-white">
                            Ready to Launch Your Fashion Empire?
                        </h2>
                        <p className="text-lg lg:text-xl mb-8 text-white/90">
                            Join thousands of successful fashion entrepreneurs who chose our platform to build their dream websites.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/register"
                                className="group inline-flex items-center justify-center px-8 py-4 bg-white text-black rounded-xl font-semibold text-lg shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                            >
                                Start Building Today
                                <Sparkles className="ml-2 w-5 h-5 group-hover:animate-pulse" />
                            </Link>
                            <Link
                                to="/templates"
                                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white rounded-xl font-semibold text-lg hover:bg-white hover:text-black transition-all duration-300"
                            >
                                Browse Templates
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Link>
                        </div>
                        <p className="mt-6 text-sm text-white/80">
                            Professional templates • Mobile responsive • SEO optimized
                        </p>
                    </motion.div>
                </div>
            </section>

            <VFooter />
        </div>
    );
};

export default WebsiteBuilderLanding;